export type SettingsTab =
	| "account"
	| "workspace"
	| "members"
	| "notifications"
	| "security"
	| "billing";

export type SettingsGroup = "you" | "workspace" | "plan";

export type MemberRole = "owner" | "admin" | "approver" | "viewer";

export type WorkspaceMember = {
	id: string;
	name: string;
	email: string;
	role: MemberRole;
	status: "active" | "invited";
};

export type RiskConfig = {
	approvalFloor: "tier-1" | "tier-2" | "tier-3";
	dailySpendCapUsd: number;
	requireStepUpForTier3: boolean;
};

export type NotificationPrefs = {
	approvalPush: boolean;
	approvalEmail: boolean;
	digestDaily: boolean;
	agentMentions: boolean;
	billingAlerts: boolean;
};

/** Unit rates — Matrix is pay-as-you-go, no subscription. */
export type PriceLine = {
	id: string;
	label: string;
	unit: string;
	/** Display price, e.g. "$3.00" */
	priceLabel: string;
	hint: string;
};

export type UsageMeter = {
	id: string;
	label: string;
	/** Human quantity, e.g. "1.2M" */
	quantityLabel: string;
	/** Cost for the period */
	costLabel: string;
	/** 0–100 for bar fill */
	sharePct: number;
};

export type UsageDay = {
	/** Short axis label */
	label: string;
	/** Relative height 0–1 */
	input: number;
	output: number;
	/** Dollar spend that day (for tooltip) */
	spendUsd: number;
};

export type PaymentMethod = {
	brand: string;
	last4: string;
	expLabel: string;
	/** false until user adds a card */
	onFile: boolean;
};

export const SETTINGS_TABS: readonly {
	id: SettingsTab;
	label: string;
	group: SettingsGroup;
}[] = [
	{ id: "account", label: "Account", group: "you" },
	{ id: "notifications", label: "Notifications", group: "you" },
	{ id: "security", label: "Security", group: "you" },
	{ id: "workspace", label: "Workspace", group: "workspace" },
	{ id: "members", label: "Members", group: "workspace" },
	{ id: "billing", label: "Billing", group: "plan" },
] as const;

export const SETTINGS_GROUPS: readonly {
	id: SettingsGroup;
	label: string;
}[] = [
	{ id: "you", label: "Settings" },
	{ id: "workspace", label: "Workspace" },
	{ id: "plan", label: "Billing" },
] as const;

/** Public rate card — Anthropic/OpenAI-style, no monthly seat fee. */
export const PRICE_LINES: readonly PriceLine[] = [
	{
		id: "tokens_in",
		label: "Model input",
		unit: "/ 1M tokens",
		priceLabel: "$3.00",
		hint: "Prompts, tool results, and agent context into the model",
	},
	{
		id: "tokens_out",
		label: "Model output",
		unit: "/ 1M tokens",
		priceLabel: "$15.00",
		hint: "Completions and streamed replies",
	},
	{
		id: "tokens_reason",
		label: "Reasoning",
		unit: "/ 1M tokens",
		priceLabel: "$15.00",
		hint: "Extended thinking when an agent opens a reasoning block",
	},
	{
		id: "cache_read",
		label: "Cache read",
		unit: "/ 1M tokens",
		priceLabel: "$0.30",
		hint: "Cached prompts that reuse prior context",
	},
	{
		id: "research",
		label: "Site research",
		unit: "/ crawl",
		priceLabel: "$0.08",
		hint: "Company research during onboarding",
	},
	{
		id: "tool_gated",
		label: "Approved action",
		unit: "/ approved action",
		priceLabel: "$0.02",
		hint: "Actions that ran after you approved them",
	},
] as const;

/** Stub month-to-date usage — replace with Langfuse / meter API. */
export const STUB_USAGE_METERS: readonly UsageMeter[] = [
	{
		id: "in",
		label: "Input tokens",
		quantityLabel: "842K",
		costLabel: "$2.53",
		sharePct: 28,
	},
	{
		id: "out",
		label: "Output tokens",
		quantityLabel: "211K",
		costLabel: "$3.17",
		sharePct: 35,
	},
	{
		id: "reason",
		label: "Reasoning tokens",
		quantityLabel: "96K",
		costLabel: "$1.44",
		sharePct: 16,
	},
	{
		id: "cache",
		label: "Cache reads",
		quantityLabel: "1.1M",
		costLabel: "$0.33",
		sharePct: 4,
	},
	{
		id: "research",
		label: "Site research",
		quantityLabel: "12",
		costLabel: "$0.96",
		sharePct: 11,
	},
	{
		id: "gated",
		label: "Gated actions",
		quantityLabel: "28",
		costLabel: "$0.56",
		sharePct: 6,
	},
] as const;

/** Last 14 days — normalized bars for the usage chart. */
export const STUB_USAGE_DAYS: readonly UsageDay[] = [
	{ label: "18", input: 0.35, output: 0.22, spendUsd: 0.41 },
	{ label: "19", input: 0.42, output: 0.28, spendUsd: 0.52 },
	{ label: "20", input: 0.28, output: 0.18, spendUsd: 0.33 },
	{ label: "21", input: 0.55, output: 0.4, spendUsd: 0.71 },
	{ label: "22", input: 0.48, output: 0.35, spendUsd: 0.62 },
	{ label: "23", input: 0.2, output: 0.12, spendUsd: 0.24 },
	{ label: "24", input: 0.15, output: 0.1, spendUsd: 0.18 },
	{ label: "25", input: 0.62, output: 0.5, spendUsd: 0.88 },
	{ label: "26", input: 0.7, output: 0.55, spendUsd: 0.95 },
	{ label: "27", input: 0.58, output: 0.45, spendUsd: 0.79 },
	{ label: "28", input: 0.8, output: 0.65, spendUsd: 1.12 },
	{ label: "29", input: 0.75, output: 0.6, spendUsd: 1.05 },
	{ label: "30", input: 0.9, output: 0.72, spendUsd: 1.28 },
	{ label: "31", input: 0.68, output: 0.52, spendUsd: 0.91 },
] as const;

export const STUB_MONTH_TOTAL_USD = 8.99;

/** Compact sidebar meter — replace with Langfuse / cost_events rollup. */
export type SidebarUsageSummary = {
	monthUsd: number;
	todayUsd: number;
	/** Human token volume label (input+output+reasoning stub). */
	tokensLabel: string;
};

export function stubSidebarUsageSummary(): SidebarUsageSummary {
	const today = STUB_USAGE_DAYS[STUB_USAGE_DAYS.length - 1];
	return {
		monthUsd: STUB_MONTH_TOTAL_USD,
		todayUsd: today?.spendUsd ?? 0,
		tokensLabel: "1.15M tok",
	};
}

export const DEFAULT_RISK: RiskConfig = {
	approvalFloor: "tier-2",
	dailySpendCapUsd: 500,
	requireStepUpForTier3: true,
};

export const DEFAULT_NOTIFICATIONS: NotificationPrefs = {
	approvalPush: true,
	approvalEmail: true,
	digestDaily: false,
	agentMentions: true,
	billingAlerts: true,
};

export const DEFAULT_PAYMENT: PaymentMethod = {
	brand: "Visa",
	last4: "",
	expLabel: "",
	onFile: false,
};

/** Clerk default org roles — custom roles (approver) come later via Dashboard. */
export type ClerkOrgRole = "org:admin" | "org:member";

export function roleLabel(role: MemberRole): string {
	switch (role) {
		case "owner":
			return "Owner";
		case "admin":
			return "Admin";
		case "approver":
			return "Approver";
		case "viewer":
			return "Member";
		default: {
			const _exhaustive: never = role;
			return _exhaustive;
		}
	}
}

export function roleHint(role: MemberRole): string {
	switch (role) {
		case "owner":
			return "Full control · billing · members";
		case "admin":
			return "Manage members and workspace config";
		case "approver":
			return "Can approve requests in connected channels";
		case "viewer":
			return "Desk access · approvals need Admin for now";
		default: {
			const _exhaustive: never = role;
			return _exhaustive;
		}
	}
}

/** Roles you can assign when inviting (Clerk only ships admin + member). */
export type InviteRole = "admin" | "viewer";

export function memberRoleToClerkOrgRole(role: InviteRole | MemberRole): ClerkOrgRole {
	switch (role) {
		case "owner":
		case "admin":
			return "org:admin";
		case "approver":
		case "viewer":
			return "org:member";
		default: {
			const _exhaustive: never = role;
			return _exhaustive;
		}
	}
}

export function clerkOrgRoleToMemberRole(role: string): MemberRole {
	if (role === "org:admin" || role === "admin") return "admin";
	return "viewer";
}

export function seedMembers(input: {
	name: string;
	email: string;
}): WorkspaceMember[] {
	return [
		{
			id: "me",
			name: input.name || "You",
			email: input.email || "you@company.com",
			role: "owner",
			status: "active",
		},
	];
}

export function formatUsd(amount: number): string {
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
	}).format(amount);
}
